import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddressIcons from '@/icons/AddressIcons'
import CountrySelect from '@/components/Form/Shared/CountrySelect'
import ProvinceSelect from '@/components/Form/Shared/ProvinceSelect'
import DistrictSelect from '@/components/Form/Shared/DistrictSelect'
import DownIcons from '@/icons/DownIcons'
import {
    getCustomerAddress,
    updateCustomerAddress,
} from '@/modules/Customer/services'
import { CustomerAddressUpdatePayload } from '@/modules/Customer/types'
import { COUNTRIES } from '@/modules/Employee/constants'
import { getCities, getDitricts } from '@/modules/Employee/services'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Collapse, FormItem } from 'vnetwork-ui'
import CloseIcons from '@/icons/CloseIcons'
import { useCustomerReadOnly } from '../../CustomerReadOnlyContext'

interface CustomerAddressProps {
    customerId: number
    refetch?: () => void
}

interface AddressFormValues {
    customer_country: string
    customer_province: string
    customer_district: string
    customer_address: string
    customer_zipcode: string
}

const CustomerAddress: React.FC<CustomerAddressProps> = ({
    customerId,
    refetch: refetchParent,
}) => {
    const { t } = useTranslation(['translation', 'customer'])
    const [isExpanded, setIsExpanded] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const isReadOnly = useCustomerReadOnly()

    const {
        data: addressData,
        renderState,
        refetch,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CUSTOMER_ADDRESS, customerId],
            queryFn: () => getCustomerAddress(customerId),
            enabled: !!customerId,
        },
        {
            showLoading: false,
            showError: false,
        }
    )

    const address = addressData?.result

    const formControl = useForm<AddressFormValues>({
        defaultValues: {
            customer_country: '',
            customer_province: '',
            customer_district: '',
            customer_address: '',
            customer_zipcode: '',
        },
    })

    // Watch form values for cascading selects
    const watchCountry = formControl.watch('customer_country')
    const watchProvince = formControl.watch('customer_province')

    // Fetch provinces based on selected country
    const { data: provinces } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.CITIES,
            watchCountry || address?.customer_country,
        ],
        queryFn: () =>
            getCities(watchCountry || address?.customer_country || ''),
        enabled: !!(watchCountry || address?.customer_country),
    })

    // Fetch districts based on selected province
    const { data: districts } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.DISTRICTS,
            watchProvince || address?.customer_province,
        ],
        queryFn: () =>
            getDitricts(watchProvince || address?.customer_province || ''),
        enabled: !!(watchProvince || address?.customer_province),
    })

    const handleUpdateAddress = async (values: AddressFormValues) => {
        return updateCustomerAddress({
            customerId,
            payload: values as CustomerAddressUpdatePayload,
        })
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: handleUpdateAddress },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMER_ADDRESS,
            successMessage: t('updated-success'),
            errorMessage: t('Something went wrong'),
            onSuccess: () => {
                refetch()
                refetchParent?.()
                setIsEditing(false)
            },
        }
    )

    // Get display labels
    const countryLabel = COUNTRIES.find(
        (c) => c.value === address?.customer_country
    )?.label
    const provinceLabel = provinces?.find(
        (p) => p.value === address?.customer_province
    )?.label
    const districtLabel = districts?.find(
        (d) => d.value === address?.customer_district
    )?.label

    // Build address summary for button display
    const addressParts = [
        address?.customer_address,
        districtLabel,
        provinceLabel,
        countryLabel,
    ].filter(Boolean)
    const addressSummary =
        addressParts.length > 0
            ? addressParts.join(', ')
            : t('customer:no-address')

    const handleEdit = () => {
        formControl.reset({
            customer_country: address?.customer_country || '',
            customer_province: address?.customer_province || '',
            customer_district: address?.customer_district || '',
            customer_address: address?.customer_address || '',
            customer_zipcode: address?.customer_zipcode || '',
        })
        setIsEditing(true)
        setIsExpanded(true)
    }

    const handleCancel = () => {
        setIsEditing(false)
        formControl.reset()
    }

    const handleSubmit = formControl.handleSubmit((values) => {
        mutateAsync(values)
    })

    const handleToggle = () => {
        if (!isEditing) {
            setIsExpanded(!isExpanded)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return null

    return (
        <div className="mt-2">
            {/* Button Header */}
            <button
                type="button"
                onClick={handleToggle}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <AddressIcons className="w-4 h-4 text-primary flex-shrink-0" />
                    <div className="flex flex-col items-start min-w-0">
                        <span className="text-xs text-gray-500">
                            {t('address')}
                        </span>
                        <span className="text-sm text-gray-700 truncate w-full text-left">
                            {addressSummary}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {!isReadOnly && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleEdit()
                            }}
                            className="!min-w-0 !p-1"
                        >
                            {t('edit')}
                        </Button>
                    )}
                    <DownIcons
                        className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                </div>
            </button>

            {/* Collapsible Content */}
            <Collapse open={isExpanded}>
                <div className="bg-white border border-t-0 border-gray-200 rounded-b-md p-4">
                    {isEditing ? (
                        // Edit Mode
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <CountrySelect
                                    control={formControl.control}
                                    name="customer_country"
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue(
                                            'customer_province',
                                            ''
                                        )
                                        formControl.setValue(
                                            'customer_district',
                                            ''
                                        )
                                    }}
                                />
                                <ProvinceSelect
                                    control={formControl.control}
                                    name="customer_province"
                                    country={watchCountry}
                                    disabled={!watchCountry}
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue(
                                            'customer_district',
                                            ''
                                        )
                                    }}
                                />
                                <DistrictSelect
                                    control={formControl.control}
                                    name="customer_district"
                                    state={watchProvince}
                                    disabled={!watchProvince}
                                />
                                <FormItem
                                    control={formControl.control}
                                    name="customer_zipcode"
                                    type="textfield"
                                    label={t('customer:zipcode')}
                                    placeholder={t('customer:zipcode')}
                                />
                                <div className="md:col-span-2">
                                    <FormItem
                                        control={formControl.control}
                                        name="customer_address"
                                        type="textfield"
                                        label={t('customer:address-detail')}
                                        placeholder={t(
                                            'customer:address-detail'
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleCancel}
                                    disabled={isPending}
                                >
                                    <CloseIcons className="w-4 h-4 mr-1" />
                                    {t('cancel')}
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    type="submit"
                                    loading={isPending}
                                    disabled={isPending}
                                >
                                    {t('save')}
                                </Button>
                            </div>
                        </form>
                    ) : (
                        // View Mode
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs text-gray-500 mb-1">
                                    {t('country')}
                                </div>
                                <div className="text-sm">
                                    {countryLabel ||
                                        address?.customer_country ||
                                        '---'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 mb-1">
                                    {t('province')}
                                </div>
                                <div className="text-sm">
                                    {provinceLabel ||
                                        address?.customer_province ||
                                        '---'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 mb-1">
                                    {t('district')}
                                </div>
                                <div className="text-sm">
                                    {districtLabel ||
                                        address?.customer_district ||
                                        '---'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 mb-1">
                                    {t('customer:zipcode')}
                                </div>
                                <div className="text-sm">
                                    {address?.customer_zipcode || '---'}
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <div className="text-xs text-gray-500 mb-1">
                                    {t('customer:address-detail')}
                                </div>
                                <div className="text-sm">
                                    {address?.customer_address || '---'}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Collapse>
        </div>
    )
}

export default CustomerAddress
